import React from 'react';
import { interactionState, type InteractionState } from '../../state';
import { useRecoilState, useSetRecoilState } from 'recoil';
import EditNodeScreen, {
  type ScreenFormState,
} from './components/EditNodeScreen';
import EditNodeEvent, {
  type EventNodeFormState,
} from './components/EditNodeEvent';
import EditEvent from './components/EditEvent';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import Modal from '~/components/organism/ModalsV2/Modal';
import { nodeById } from '../../state/nodesAndEvents';
import CreateScreenNode from './components/CreateScreenNode';
import PromptWidgetURL from './components/PromptWidgetURL';

type Props = {};

const InteractionHandler: React.FCC<Props> = () => {
  const [interaction, setInteraction] = useRecoilState(interactionState);
  const subjectId =
    interaction && 'subjectId' in interaction
      ? interaction.subjectId ?? ''
      : '';
  const setNode = useSetRecoilState(nodeById(subjectId));

  if (interaction === null) return null;
  const Component = getInteractionComp(interaction);

  if (Component === null) return null;

  const onCancel = () => setInteraction(null);
  const onSave = (update: ScreenFormState | EventNodeFormState) => {
    setNode(update);
    setInteraction(null);
  };

  return (
    <Overlay onClose={onCancel}>
      <Modal
        onClose={onCancel}
        maxWidth={interactionToModalWidthMapping[interaction.interactionType]}
      >
        <Component id={subjectId} onCancel={onCancel} onSave={onSave} />
      </Modal>
    </Overlay>
  );
};

const interactionToModalWidthMapping: Record<
  InteractionState['interactionType'],
  string
> = {
  'create-node-screen': '720px',
  'prompt-url': '720px',
  'edit-event': '90%',
  'edit-node-event': '90%',
  'edit-node-screen': '90%',
};

export type EditProps<T> = {
  id: string;
  onCancel: () => void;
  onSave: (update: T) => void;
};

const interactionMap: Record<
  InteractionState['interactionType'],
  React.FC<EditProps<ScreenFormState | EventNodeFormState>>
> = {
  'edit-event': EditEvent,
  'edit-node-event': EditNodeEvent,
  'edit-node-screen': EditNodeScreen,
  'create-node-screen': CreateScreenNode,
  'prompt-url': PromptWidgetURL,
};

const getInteractionComp = (interaction: InteractionState) =>
  interaction ? interactionMap[interaction.interactionType] : null;

export default InteractionHandler;
